import { useState } from 'react';
import { track } from '../services/analyticsTracker';
import { 
  Key, Lock, Unlock, Clock, User, CheckCircle, AlertCircle,
  QrCode, Copy, Send, History, Smartphone, DoorOpen
} from 'lucide-react';

interface AccessCode {
  id: string;
  code: string;
  type: 'temporary' | 'permanent' | 'service';
  guestName?: string;
  propertyName: string;
  validFrom: string;
  validUntil: string;
  status: 'active' | 'expired' | 'revoked';
  usageCount: number;
  lastUsed?: string;
}

interface AccessLog {
  id: string;
  timestamp: string;
  user: string;
  action: 'entry' | 'exit';
  method: 'code' | 'app' | 'key';
}

export default function SmartAccess() {
  const [codes, setCodes] = useState<AccessCode[]>([]);

  const [logs] = useState<AccessLog[]>([]);

  const [showNewCode, setShowNewCode] = useState(false);
  const [newCode, setNewCode] = useState({
    type: 'temporary' as const,
    guestName: '',
    validFrom: '',
    validUntil: '',
  });
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  const generateCode = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const handleCreateCode = () => {
    const code = generateCode();
    setGeneratedCode(code);
    track('access_code_created');
    
    const accessCode: AccessCode = {
      id: Date.now().toString(),
      code,
      type: newCode.type,
      guestName: newCode.guestName || undefined,
      propertyName: '', // no default property without real data
      validFrom: newCode.validFrom,
      validUntil: newCode.validUntil,
      status: 'active',
      usageCount: 0,
    };
    
    setCodes([accessCode, ...codes]);
  };

  const handleRevoke = (id: string) => {
    setCodes(codes.map(c => c.id === id ? { ...c, status: 'revoked' as const } : c));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Código copiado: ' + text);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      expired: 'bg-slate-500/20 text-slate-400',
      revoked: 'bg-red-500/20 text-red-400',
    };
    const labels = {
      active: 'Activo',
      expired: 'Expirado',
      revoked: 'Revocado',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  if (codes.length === 0 && logs.length === 0) return (
    <div className="p-6"><div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
        <p className="font-semibold text-[var(--v-text-primary)]">Sin datos todavía</p>
        <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Esta sección se llenará con información real cuando registres propiedades y actividad.</p>
      </div></div>
  );

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setShowNewCode(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-xl font-medium flex flex-col items-center gap-2"
        >
          <Key className="w-6 h-6" />
          Generar Código
        </button>
        <button className="bg-slate-700 hover:bg-slate-600 text-white p-4 rounded-xl font-medium flex flex-col items-center gap-2">
          <History className="w-6 h-6" />
          Ver Historial
        </button>
      </div>

      {/* Active Codes */}
      <div>
        <h3 className="font-semibold text-white mb-3">Códigos Activos</h3>
        <div className="space-y-3">
          {codes.filter(c => c.status === 'active').map((code) => (
            <div key={code.id} className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{code.code}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">
                        {code.type === 'temporary' ? code.guestName : 'Servicio de Limpieza'}
                      </h4>
                      {getStatusBadge(code.status)}
                    </div>
                    <p className="text-slate-400 text-sm">{code.propertyName}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {code.validFrom} → {code.validUntil}
                      </span>
                      <span>•</span>
                      <span>{code.usageCount} usos</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(code.code)}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
                    title="Copiar código"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleRevoke(code.id)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                    title="Revocar acceso"
                  >
                    <Lock className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Property Status */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4">Estado de Propiedades</h3>
        <p className="text-slate-400 text-sm">Sin accesos activos.</p>
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-surface-3)]">
          <h3 className="font-semibold text-white">Actividad Reciente</h3>
        </div>
        <div className="divide-y divide-slate-700">
          {logs.map((log) => (
            <div key={log.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  log.action === 'entry' ? 'bg-green-500/20' : 'bg-blue-500/20'
                }`}>
                  {log.action === 'entry' ? (
                    <DoorOpen className="w-5 h-5 text-green-400" />
                  ) : (
                    <Lock className="w-5 h-5 text-blue-400" />
                  )}
                </div>
                <div>
                  <p className="text-white">{log.user}</p>
                  <p className="text-slate-400 text-sm">{log.action === 'entry' ? 'Entrada' : 'Salida'} • {log.method}</p>
                </div>
              </div>
              <span className="text-slate-500 text-sm">{log.timestamp}</span>
            </div>
          ))}
        </div>
      </div>

      {/* New Code Modal */}
      {showNewCode && (
        <div className="fixed inset-0 bg-slate-900/90 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] w-full max-w-md">
            <div className="p-4 border-b border-[var(--v-surface-3)]">
              <h3 className="text-lg font-semibold text-white">Generar Código de Acceso</h3>
            </div>
            <div className="p-4 space-y-4">
              {!generatedCode ? (
                <>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Tipo de Acceso</label>
                    <select
                      value={newCode.type}
                      onChange={(e) => setNewCode({...newCode, type: e.target.value as any})}
                      className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600"
                    >
                      <option value="temporary">Huésped (Temporal)</option>
                      <option value="service">Servicio de Limpieza</option>
                      <option value="permanent">Permanente</option>
                    </select>
                  </div>
                  {newCode.type === 'temporary' && (
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Nombre del Huésped</label>
                      <input
                        type="text"
                        value={newCode.guestName}
                        onChange={(e) => setNewCode({...newCode, guestName: e.target.value})}
                        placeholder="Nombre completo"
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Válido desde</label>
                      <input
                        type="datetime-local"
                        value={newCode.validFrom}
                        onChange={(e) => setNewCode({...newCode, validFrom: e.target.value})}
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">Válido hasta</label>
                      <input
                        type="datetime-local"
                        value={newCode.validUntil}
                        onChange={(e) => setNewCode({...newCode, validUntil: e.target.value})}
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-slate-400 mb-4">Código generado:</p>
                  <div className="text-5xl font-bold text-white tracking-widest mb-4">
                    {generatedCode}
                  </div>
                  <div className="w-32 h-32 bg-[var(--v-surface-1)] mx-auto rounded-lg flex items-center justify-center mb-4">
                    <QrCode className="w-24 h-24 text-slate-900" />
                  </div>
                  <p className="text-slate-400 text-sm">Comparte este código con el huésped</p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowNewCode(false);
                    setGeneratedCode(null);
                  }}
                  className="flex-1 py-3 rounded-lg text-slate-300 hover:text-white"
                >
                  {generatedCode ? 'Cerrar' : 'Cancelar'}
                </button>
                {!generatedCode && (
                  <button
                    onClick={handleCreateCode}
                    disabled={!newCode.validFrom || !newCode.validUntil}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium disabled:opacity-50"
                  >
                    Generar
                  </button>
                )}
                {generatedCode && (
                  <button
                    onClick={() => {
                      copyToClipboard(generatedCode);
                      setShowNewCode(false);
                      setGeneratedCode(null);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Copiar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
